import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  ArrowLeft,
  Pencil,
  Truck,
  CheckCircle,
  Link2,
  ExternalLink,
  Warehouse,
  Clock,
  CheckCircle2,
  Hourglass,
  RefreshCw,
  Split,
  Receipt,
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  Package,
  Loader2,
  X,
  Factory,
  Trash2,
  Snowflake,
} from 'lucide-react';
import Layout from '../components/Layout';
import SoPoSplittingDialog from '../components/SoPoSplittingDialog';
import UnifiedConversionDialog from '../components/UnifiedConversionDialog';
import LinkPODialog from '../components/LinkPODialog';
import FileAttachmentManager from '../components/FileAttachmentManager';
import CreateOutboundDialog from '../components/warehouse/CreateOutboundDialog';
import ProductInventoryDrawer from '../components/ProductInventoryDrawer';
import ProductTable from '../components/ProductTable';
import TemplateSelector from '../components/TemplateSelector';
import BulkDownloadProductAttachmentsButton from '../components/BulkDownloadProductAttachmentsButton';
import excelExportService from '../services/excelExportService';
import salesService from '../services/salesService';
import authService from '../services/authService';
import purchaseService from '../services/purchaseService';
import productionService from '../services/productionService';

const SalesDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.orderData || null);
  const [loading, setLoading] = useState(!location.state?.orderData);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', order: null });
  const [splittingDialog, setSplittingDialog] = useState({ open: false });
  const [unifiedConversionOpen, setUnifiedConversionOpen] = useState(false);
  const [linkPODialog, setLinkPODialog] = useState({ open: false });
  const [unlinkDialog, setUnlinkDialog] = useState({ open: false, po: null });
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [outboundDialogOpen, setOutboundDialogOpen] = useState(false);
  const [linkedPOs, setLinkedPOs] = useState([]);
  const [linkedPRDs, setLinkedPRDs] = useState([]);
  const [expandedProducts, setExpandedProducts] = useState({});

  // Product Inventory Drawer state
  const [inventoryDrawer, setInventoryDrawer] = useState({
    open: false,
    productCode: null,
    productName: null
  });

  const [executionProgress, setExecutionProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const user = authService.getCurrentUser();
    setUserRole(user?.role || '');
  }, []);

  const loadOrder = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const response = await salesService.getSalesOrder(id);
      setOrder(response);
    } catch (err) {
      setError(err.response?.data?.error || '加载订单详情失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadExecutionProgress = useCallback(async () => {
    if (!id) return;

    try {
      setLoadingProgress(true);
      const response = await salesService.getExecutionProgress(id);
      setExecutionProgress(response);

      if (response && response.per_product) {
        const allLinkedPOs = [];
        Object.values(response.per_product).forEach(product => {
          if (product.linked_pos) {
            product.linked_pos.forEach(po => {
              if (!allLinkedPOs.find(p => p.po_id === po.po_id)) {
                allLinkedPOs.push(po);
              }
            });
          }
        });
        const formattedPOs = allLinkedPOs.map(po => ({ _id: po.po_id, purchase_order_id: po.po_number }));
        setLinkedPOs(formattedPOs);

        // Extract linked production orders
        const allLinkedPRDs = [];
        Object.values(response.per_product).forEach(product => {
          if (product.production && product.production.linked_prds) {
            product.production.linked_prds.forEach(prd => {
              if (!allLinkedPRDs.find(p => p.production_order_id === prd.production_order_id)) {
                allLinkedPRDs.push(prd);
              }
            });
          }
        });
        setLinkedPRDs(allLinkedPRDs);
      }
    } catch (err) {
      console.error('Error loading execution progress:', err);
    } finally {
      setLoadingProgress(false);
    }
  }, [id]);

  const computeProcurementSummary = (executionProgress) => {
    if (!executionProgress?.per_product) return null;

    let totalRequired = 0;
    let totalAllocated = 0;
    let totalReceived = 0;

    Object.values(executionProgress.per_product).forEach(product => {
      totalRequired += product.needs_procurement || 0;
      totalAllocated += product.allocated_to_po || 0;
      totalReceived += product.received_qty || 0;
    });

    const allocatedPct = totalRequired > 0 ? (totalAllocated / totalRequired) * 100 : 0;
    const receivedPct = totalRequired > 0 ? (totalReceived / totalRequired) * 100 : 0;

    let status = 'not_started';
    if (totalReceived >= totalRequired && totalRequired > 0) {
      status = 'complete';
    } else if (totalAllocated > 0 || totalReceived > 0) {
      status = 'partial';
    }

    return {
      status,
      totals: {
        required_qty: totalRequired,
        allocated_qty: totalAllocated,
        received_qty: totalReceived
      },
      allocated_completion_pct: allocatedPct,
      received_completion_pct: receivedPct
    };
  };

  useEffect(() => {
    loadOrder();
    loadExecutionProgress();
  }, [loadOrder, loadExecutionProgress]);

  const handleAction = (action) => {
    if (action === 'edit') {
      navigate(`/sales/${id}/edit`);
    } else if (action === 'ship' || action === 'confirm' || action === 'invoice') {
      setConfirmDialog({ open: true, action, order });
    } else if (action === 'convert' || action === 'split') {
      setSplittingDialog({ open: true });
    }
  };

  const handleConfirmAction = async () => {
    const { action, order: orderData } = confirmDialog;
    setConfirmDialog({ open: false, action: '', order: null });

    try {
      if (action === 'ship') {
        await salesService.shipOrder(orderData._id);
        setOrder(prev => ({ ...prev, status: 'shipped' }));
      } else if (action === 'confirm') {
        await salesService.confirmSalesOrder(orderData._id);
        setOrder(prev => ({ ...prev, status: 'confirmed' }));
      } else if (action === 'invoice') {
        await salesService.invoiceOrder(orderData._id);
        setOrder(prev => ({ ...prev, status: 'invoiced' }));
      }
    } catch (err) {
      const actionText = action === 'ship' ? '发货' : action === 'invoice' ? '开票' : '确认';
      setError(err.response?.data?.error || `${actionText}订单失败`);
    }
  };

  const handleDeletePRD = async (orderId) => {
    try {
      await productionService.deleteProductionOrder(orderId);
      // Remove from linkedPRDs
      setLinkedPRDs(prev => prev.filter(prd => prd._id !== orderId));
      alert('工单已永久删除');
    } catch (err) {
      setError('删除工单失败: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    }
  };

  const handleSplittingComplete = (result) => {
    setSplittingDialog({ open: false });
    if (result.success) {
      const poIds = result.created_purchase_orders.map(po => po.purchase_order_id).join(', ');
      alert(`成功创建 ${result.created_purchase_orders.length} 个采购订单：${poIds}`);
      loadExecutionProgress();
    }
  };

  const handleTemplateSelected = async (template) => {
    try {
      const { blob, filename } = await excelExportService.exportUsingTemplate('sales', {
        template_id: template.template_id,
        sales_order_ids: [order._id]
      });

      excelExportService.downloadBlob(blob, filename);
    } catch (err) {
      setError('导出失败: ' + (err.message || 'Unknown error'));
    }
    setTemplateSelectorOpen(false);
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'default';
      case 'shipped': return 'secondary';
      case 'invoiced': return 'outline';
      case 'delivered': return 'default';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-success/10 text-success border-success/30';
      case 'shipped': return 'bg-info/10 text-info border-info/30';
      case 'invoiced': return 'bg-secondary/10 text-secondary border-secondary/30';
      case 'delivered': return 'bg-primary/10 text-primary border-primary/30';
      case 'cancelled': return 'bg-destructive/10 text-destructive border-destructive/30';
      default: return '';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'new': return '新建';
      case 'draft': return '草稿';
      case 'pending': return '待确认';
      case 'confirmed': return '已确认';
      case 'shipped': return '已发货';
      case 'delivered': return '已送达';
      case 'invoiced': return '已开票';
      case 'cancelled': return '已取消';
      case 'completed': return '已完成';
      default: return status || '未知';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const handleToggleProduct = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handleCollapseAll = () => setExpandedProducts({});

  const handleExpandAll = () => {
    const allExpanded = {};
    order?.products?.forEach(p => {
      allExpanded[p._id || p.product_code] = true;
    });
    setExpandedProducts(allExpanded);
  };

  const getProgressColor = (pct) => {
    if (pct >= 90) return 'bg-success';
    if (pct >= 50) return 'bg-warning';
    return 'bg-destructive';
  };

  if (loading) {
    return (
      <Layout title="销售订单详情" activeTab="sales">
        <div className="max-w-7xl mx-auto py-8 px-4">
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout title="销售订单详情" activeTab="sales">
        <div className="max-w-7xl mx-auto py-8 px-4">
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground mb-4">
              订单未找到
            </p>
            <Button onClick={() => navigate('/sales')}>
              返回销售列表
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const summary = computeProcurementSummary(executionProgress);

  return (
    <Layout title="销售订单详情" activeTab="sales">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/sales')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              销售订单详情
            </h1>
            <p className="text-sm text-muted-foreground">
              订单号：{order.sales_order_id}
            </p>
          </div>
        </div>

        {/* Order Information - Compact */}
        <Card className="border bg-card mb-4">
          <CardContent className="p-4">
            <h2 className="text-base font-semibold mb-3">订单信息</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">订单编号</p>
                <p className="font-medium truncate">{order.sales_order_id}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">客户名称</p>
                <p className="font-medium truncate">{order.client_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">状态</p>
                <Badge variant={getStatusVariant(order.status)} className={getStatusClass(order.status)}>
                  {getStatusLabel(order.status)}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">产品总数</p>
                <p className="font-medium">{order.total_products || order.products?.length || 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">订单日期</p>
                <p className="font-medium">{formatDate(order.order_date)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">价格栏</p>
                <p className="font-medium">{order.price_column || 'N/A'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">
                  {order.pricing_mode === 'tax_exclusive' ? '合计金额（不含税）' : '合计金额（含税）'}
                </p>
                {authService.canViewSalesPrices() ? (
                  <>
                    <p className="text-lg font-bold text-primary">
                      {formatCurrency(order.total_price)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      含税：{formatCurrency(Math.round((order.total_price || 0) * 1.13 * 100) / 100)}
                      <span className="ml-2">税额：{formatCurrency(Math.round((order.total_price || 0) * 0.13 * 100) / 100)}</span>
                    </p>
                  </>
                ) : (
                  <p className="text-lg font-bold text-muted-foreground">*** 无权查看 ***</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Procurement Status Section */}
        <Card className={cn("border bg-card mb-6", executionProgress && "border-info")}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                采购执行状态
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={loadExecutionProgress}
                disabled={loadingProgress}
              >
                {loadingProgress ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                刷新状态
              </Button>
            </div>

            {loadingProgress ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : summary ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Overall Status */}
                <div className="text-center p-4 bg-info/10 rounded-lg">
                  <p className="text-2xl mb-1">
                    {summary.status === 'complete' ? '✅' : summary.status === 'partial' ? '⏳' : '⏸️'}
                  </p>
                  <p className="text-xl font-bold text-primary">
                    {summary.status === 'complete' ? '已完成' : summary.status === 'partial' ? '进行中' : '未开始'}
                  </p>
                  {(Number(summary.totals?.allocated_qty || 0) >= Number(summary.totals?.required_qty || 0)) && Number(summary.totals?.received_qty || 0) === 0 && (
                    <p className="text-xs text-warning mt-1">已全部分配（待收货）</p>
                  )}
                  <p className="text-sm text-muted-foreground">总体状态</p>
                </div>

                {/* Progress Statistics */}
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">分配完成度</p>
                  <p className="text-xl font-bold text-primary">
                    {summary.allocated_completion_pct?.toFixed(1) || '0.0'}%
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">
                    {summary.totals?.allocated_qty || 0} / {summary.totals?.required_qty || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">收货完成度</p>
                  <p className="text-xl font-bold text-success">
                    {summary.received_completion_pct?.toFixed(1) || '0.0'}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.totals?.received_qty || 0} / {summary.totals?.required_qty || 0}
                  </p>
                </div>

                <div className="text-center p-4 bg-warning/10 rounded-lg">
                  <p className="text-xl font-bold text-info">{linkedPOs.length}</p>
                  <p className="text-sm text-muted-foreground">关联采购单</p>
                </div>

                {/* Linked Purchase Orders */}
                {linkedPOs.length > 0 && (
                  <div className="md:col-span-3">
                    <h3 className="font-medium mb-4">关联采购订单</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {linkedPOs.map((po, index) => (
                        <Card
                          key={po._id || po.purchase_order_id || index}
                          className="border cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => navigate(`/purchases/${po._id}/view`)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-primary">{po.purchase_order_id}</span>
                              <div className="flex items-center gap-2">
                                <ExternalLink className="h-4 w-4 text-muted-foreground" />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground h-7 text-xs"
                                  onClick={(e) => { e.stopPropagation(); setUnlinkDialog({ open: true, po }); }}
                                >
                                  取消关联并删除
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{po.supplier_name}</p>
                            <div className="flex items-center gap-2 mb-2">
                              {po.status === 'confirmed' ? <CheckCircle2 className="h-4 w-4 text-success" /> :
                               po.status === 'submitted' ? <Clock className="h-4 w-4 text-info" /> :
                               <Hourglass className="h-4 w-4 text-muted-foreground" />}
                              <Badge variant="outline" className={cn(
                                po.status === 'confirmed' ? 'bg-success/10 text-success' :
                                po.status === 'submitted' ? 'bg-info/10 text-info' : ''
                              )}>
                                {po.status === 'confirmed' ? '已确认' :
                                 po.status === 'submitted' ? '已提交' : '草稿'}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {po.total_products || '未知'} 项产品
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">此销售订单尚未关联任何采购订单</p>
                <p className="text-sm text-muted-foreground mt-2">
                  使用"转为采购订单"功能创建关联的采购订单
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Linked Orders Section - Parallel Layout */}
        {(linkedPOs.length > 0 || linkedPRDs.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Linked Purchase Orders */}
            {linkedPOs.length > 0 && (
              <Card className="border bg-card border-info/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Link2 className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">关联采购订单</h3>
                    <Badge variant="outline">{linkedPOs.length}</Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {linkedPOs.map((po, index) => (
                      <Card
                        key={po._id || po.purchase_order_id || index}
                        className="border cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/purchases/${po._id}/view`)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-primary text-sm">{po.purchase_order_id}</span>
                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{po.supplier_name}</p>
                          <div className="flex items-center gap-1 mt-2">
                            {po.status === 'confirmed' ? <CheckCircle2 className="h-3 w-3 text-success" /> :
                             po.status === 'submitted' ? <Clock className="h-3 w-3 text-info" /> :
                             <Hourglass className="h-3 w-3 text-muted-foreground" />}
                            <Badge variant="outline" className={cn(
                              "text-xs h-5",
                              po.status === 'confirmed' ? 'bg-success/10 text-success' :
                              po.status === 'submitted' ? 'bg-info/10 text-info' : ''
                            )}>
                              {po.status === 'confirmed' ? '已确认' :
                               po.status === 'submitted' ? '已提交' : '草稿'}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Linked Production Orders */}
            {linkedPRDs.length > 0 && (
              <Card className="border bg-card border-purple-500/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Factory className="h-5 w-5 text-purple-500" />
                    <h3 className="font-semibold">关联生产订单</h3>
                    <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
                      {linkedPRDs.length}
                    </Badge>
                  </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {linkedPRDs.map((prd) => (
                        <Card
                          key={prd._id}
                          className="border-purple-200 hover:shadow-md transition-shadow"
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between mb-1">
                              <div 
                                className="flex items-center gap-1 cursor-pointer hover:underline"
                                onClick={() => navigate(`/production/${prd._id}`)}
                              >
                                <span className="font-medium text-purple-600 text-sm">{prd.production_order_id}</span>
                                <ExternalLink className="h-3 w-3 text-muted-foreground" />
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className={cn(
                                  "text-xs h-5",
                                  prd.status === 'completed' ? 'bg-success/10 text-success' :
                                  prd.status === 'in_progress' ? 'bg-info/10 text-info' : 'bg-muted'
                                )}>
                                  {prd.status === 'completed' ? '已完成' :
                                   prd.status === 'in_progress' ? '生产中' : 
                                   prd.status === 'released' ? '已下发' : prd.status}
                                </Badge>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`确定要永久删除工单 ${prd.production_order_id} 吗？此操作不可恢复！`)) {
                                      handleDeletePRD(prd._id);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              <p>计划: {prd.planned_quantity} 件</p>
                              <p>完成: {prd.completed_quantity} 件</p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Product Line Execution Progress */}
        {order.products?.length > 0 && (
          <Card className="border bg-card mb-6">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-semibold">📊 行项目执行进度</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    跟踪每个产品从库存到采购、入库、出库的完整履约流程
                  </p>
                </div>
                <div className="flex gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={loadExecutionProgress}
                          disabled={loadingProgress}
                        >
                          {loadingProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>刷新最新执行进度</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Button variant="outline" size="sm" onClick={handleCollapseAll}>收起全部</Button>
                  <Button variant="outline" size="sm" onClick={handleExpandAll}>展开全部</Button>
                </div>
              </div>

              {/* Legend */}
              <div className="flex gap-6 mb-4 p-2 bg-muted rounded-lg">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-success rounded" />
                  <span className="text-xs text-muted-foreground">库存满足</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-info rounded" />
                  <span className="text-xs text-muted-foreground">采购进度</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-muted-foreground/30 rounded" />
                  <span className="text-xs text-muted-foreground">待处理</span>
                </div>
              </div>

              {/* Product rows */}
              <div className="space-y-3">
                {order.products.map((product, index) => {
                  // Use line_item_id for unique key (supports duplicate product_id + specification)
                  const productKey = product.line_item_id || `${product._id || product.product_code}-${index}`;
                  const isExpanded = expandedProducts[productKey];
                  const productCode = product.product_code || product.product_id;

                  let inventoryData = null;
                  let productionData = null;
                  let procurementData = {
                    allocated_qty: 0,
                    received_qty: 0,
                    pending_qty: 0,
                    required_qty: 0,
                    pending_po_creation: 0
                  };
                  let warehouseData = {
                    inbound: { received_qty: 0, pending_qty: 0, movements: [] },
                    outbound: { shipped_qty: 0, pending_qty: 0, movements: [] }
                  };
                  let breakdown = null;
                  let required = product.quantity ?? 0;

                  if (executionProgress?.per_product?.[productCode]) {
                    const progressData = executionProgress.per_product[productCode];

                    inventoryData = {
                      available_quantity: progressData.stock_at_creation,
                      reserved_quantity: progressData.frozen_qty || 0,
                      remaining_quantity: progressData.stock_at_creation - (progressData.frozen_qty || 0)
                    };

                    productionData = progressData.production;

                    procurementData = {
                      allocated_qty: progressData.allocated_to_po,
                      received_qty: progressData.received_qty,
                      pending_qty: progressData.pending_receipt,
                      required_qty: progressData.required_qty,
                      pending_po_creation: progressData.pending_po_creation
                    };

                    warehouseData = {
                      inbound: {
                        received_qty: progressData.received_qty,
                        pending_qty: progressData.pending_receipt,
                        movements: progressData.inbound_movements || []
                      },
                      outbound: {
                        shipped_qty: progressData.shipped_qty,
                        pending_qty: progressData.pending_shipment,
                        movements: progressData.outbound_movements || []
                      }
                    };

                    breakdown = {
                      from_stock: progressData.from_stock,
                      from_procurement: progressData.needs_procurement,
                      stock_coverage_pct: (progressData.from_stock / progressData.required_qty) * 100,
                      overall_progress_pct: progressData.progress?.overall_progress_pct || 0,
                      stockCoverage: progressData.from_stock,
                      stockFulfilledPct: progressData.required_qty > 0
                        ? (progressData.from_stock / progressData.required_qty) * 100
                        : 0,
                      procurementAllocated: progressData.allocated_to_po,
                      procurementFulfilledPct: progressData.needs_procurement > 0
                        ? (progressData.allocated_to_po / progressData.needs_procurement) * 100
                        : 0
                    };

                    required = progressData.required_qty;
                  }

                  let overallProgress = 0;
                  if (breakdown && breakdown.overall_progress_pct !== undefined) {
                    overallProgress = breakdown.overall_progress_pct;
                  } else if (executionProgress?.per_product?.[productCode]) {
                    overallProgress = executionProgress.per_product[productCode].progress?.overall_progress_pct || 0;
                  }

                  return (
                    <div key={productKey}>
                      {/* Collapsed row */}
                      <div
                        className={cn(
                          "p-3 rounded-lg cursor-pointer transition-all",
                          isExpanded ? "bg-primary/10" : "bg-muted",
                          "hover:bg-primary/20 hover:shadow-sm"
                        )}
                        onClick={() => handleToggleProduct(productKey)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                            <span className="text-sm font-medium">{productCode}</span>
                            <span className="text-sm text-muted-foreground">{product.name || product.product_name}</span>
                          </div>
                          <span className="text-sm text-muted-foreground">需求数量: {required} 件</span>
                        </div>

                        {/* Progress bar */}
                        <div className="ml-8 mt-2">
                          <div className="flex items-center gap-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex-1 h-2 bg-muted-foreground/20 rounded-full overflow-hidden flex">
                                    {breakdown ? (
                                      <>
                                        <div
                                          className="h-full bg-success transition-all"
                                          style={{ width: `${breakdown.stockFulfilledPct}%` }}
                                        />
                                        <div
                                          className="h-full bg-info transition-all"
                                          style={{ width: `${breakdown.procurementFulfilledPct}%` }}
                                        />
                                        {/* NEW: Production segment */}
                                        {productionData?.allocated_qty > 0 && (
                                          <div className="h-full bg-purple-500 transition-all" 
                                               style={{ width: `${Math.min(100, (productionData.completed_qty / productionData.allocated_qty) * 100)}%` }} />
                                        )}
                                      </>
                                    ) : (
                                      <div
                                        className={cn("h-full transition-all", getProgressColor(overallProgress))}
                                        style={{ width: `${overallProgress}%` }}
                                      />
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {breakdown ? (
                                    <div className="text-xs">
                                      <p className="mb-1">履约进度明细:</p>
                                      <p>• 库存满足: {breakdown.stockCoverage} 件 ({breakdown.stockFulfilledPct.toFixed(1)}%)</p>
                                      <p>• 采购完成: {breakdown.procurementAllocated} 件 ({breakdown.procurementFulfilledPct.toFixed(1)}%)</p>
                                      <p className="mt-1">总体完成度: {overallProgress}%</p>
                                    </div>
                                  ) : (
                                    <p>整体履约进度: {overallProgress}%</p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <span className={cn(
                              "text-xs font-medium min-w-[45px]",
                              overallProgress >= 100 ? "text-success" : overallProgress >= 50 ? "text-primary" : "text-muted-foreground"
                            )}>
                              {overallProgress}%
                            </span>
                          </div>
                          {breakdown && (
                            <div className="flex gap-4 mt-1">
                              <div className="flex items-center gap-1">
                                <div className="w-2.5 h-2.5 bg-success rounded" />
                                <span className="text-xs text-muted-foreground">库存覆盖 {breakdown.stockCoverage}/{required} 件</span>
                              </div>
                              {breakdown.from_procurement > 0 && (
                                <div className="flex items-center gap-1">
                                  <div className="w-2.5 h-2.5 bg-info rounded" />
                                  <span className="text-xs text-muted-foreground">采购进度 {breakdown.procurementAllocated}/{breakdown.from_procurement} 件</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                       {/* Expanded content */}
                       {isExpanded && (
                         <div className="p-3 bg-muted mt-1 rounded-lg border">
                           <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
                             {/* Column 1: Inventory */}
                             {inventoryData && (
                               <div>
                                 <div className="flex items-center gap-1 mb-2">
                                   <Package className="h-4 w-4 text-success" />
                                   <span className="text-sm font-semibold">库存状态</span>
                                 </div>
                                 <div className="space-y-1 text-xs">
                                   <div className="flex justify-between">
                                     <span className="text-muted-foreground">可用</span>
                                    <span
                                      className={cn(
                                        "font-medium cursor-pointer hover:underline",
                                        (inventoryData.available_quantity || 0) >= required ? "text-success" :
                                        (inventoryData.available_quantity || 0) > 0 ? "text-warning" : "text-destructive"
                                      )}
                                      onClick={() => setInventoryDrawer({
                                        open: true,
                                        productCode: productCode,
                                        productName: product.name || product.product_name
                                      })}
                                      title="点击查看库存详情"
                                    >
                                      {inventoryData.available_quantity || 0} 件
                                    </span>
                                   </div>
                                   <div className="flex justify-between">
                                     <span className="text-muted-foreground">满足</span>
                                     <span className="font-medium text-info">{breakdown?.stockCoverage || 0} 件</span>
                                   </div>
                                   <div className="flex justify-between">
                                     <span className="text-muted-foreground">预留</span>
                                     <span className="text-muted-foreground">{inventoryData.reserved_quantity || 0}</span>
                                   </div>
                                   <div className="mt-1">
                                     <Progress
                                       value={required > 0 ? Math.min(100, (breakdown?.stockCoverage || 0) / required * 100) : 0}
                                       className="h-1"
                                     />
                                     <span className="text-xs text-muted-foreground">
                                       {required > 0 ? Math.round((breakdown?.stockCoverage || 0) / required * 100) : 0}%
                                     </span>
                                   </div>
                                 </div>
                               </div>
                             )}

                             {/* Column 2: Procurement */}
                             <div>
                               <div className="flex items-center gap-1 mb-2">
                                 <ShoppingCart className="h-4 w-4 text-primary" />
                                 <span className="text-sm font-semibold">采购分配</span>
                               </div>
                               <div className="space-y-1 text-xs">
                                 <div className="flex justify-between">
                                   <span className="text-muted-foreground">已分配</span>
                                   <span className="font-medium text-success">{procurementData.allocated_qty || 0}</span>
                                 </div>
                                 <div className="flex justify-between">
                                   <span className="text-muted-foreground">待创建</span>
                                   <span className="text-muted-foreground">{procurementData.pending_po_creation || 0}</span>
                                 </div>
                                 <div className="mt-1">
                                   <Progress
                                     value={(breakdown?.from_procurement || required) > 0 ? (procurementData.allocated_qty || 0) / (breakdown?.from_procurement || required) * 100 : 0}
                                     className="h-1"
                                   />
                                   <span className="text-xs text-muted-foreground">
                                     {(breakdown?.from_procurement || required) > 0 ? Math.round((procurementData.allocated_qty || 0) / (breakdown?.from_procurement || required) * 100) : 0}%
                                   </span>
                                 </div>
                               </div>
                             </div>

                             {/* NEW: Production Column */}
                             {productionData && productionData.allocated_qty > 0 && (
                               <div>
                                 <div className="flex items-center gap-1 mb-2">
                                   <Factory className="h-4 w-4 text-purple-500" />
                                   <span className="text-sm font-semibold">生产</span>
                                 </div>
                                 <div className="space-y-1 text-xs">
                                   <div className="flex justify-between">
                                     <span className="text-muted-foreground">计划</span>
                                     <span className="font-medium">{productionData.allocated_qty}</span>
                                   </div>
                                   <div className="flex justify-between">
                                     <span className="text-muted-foreground">完成</span>
                                     <span className="font-medium text-purple-600">{productionData.completed_qty}</span>
                                   </div>
                                   <div className="mt-1">
                                     <Progress 
                                       value={productionData.allocated_qty > 0 ? (productionData.completed_qty / productionData.allocated_qty) * 100 : 0}
                                       className="h-1 bg-purple-200 [&>div]:bg-purple-500" 
                                     />
                                     <span className="text-xs text-purple-600">
                                       {productionData.allocated_qty > 0 ? Math.round((productionData.completed_qty / productionData.allocated_qty) * 100) : 0}%
                                     </span>
                                   </div>
                                 </div>
                               </div>
                             )}

                             {/* Column 3: Inbound */}
                             <div>
                               <div className="flex items-center gap-1 mb-2">
                                 <Package className="h-4 w-4 text-info" />
                                 <span className="text-sm font-semibold">入库接收</span>
                               </div>
                               <div className="space-y-1 text-xs">
                                 <div className="flex justify-between">
                                   <span className="text-muted-foreground">已入库</span>
                                   <span className="font-medium text-success">{procurementData.received_qty || 0}</span>
                                 </div>
                                 <div className="flex justify-between">
                                   <span className="text-muted-foreground">待收货</span>
                                   <span className="text-muted-foreground">{procurementData.pending_qty || 0}</span>
                                 </div>
                                 <div className="mt-1">
                                   <Progress
                                     value={(breakdown?.from_procurement || required) > 0 ? (procurementData.received_qty || 0) / (breakdown?.from_procurement || required) * 100 : 0}
                                     className="h-1"
                                   />
                                   <span className="text-xs text-muted-foreground">
                                     {(breakdown?.from_procurement || required) > 0 ? Math.round((procurementData.received_qty || 0) / (breakdown?.from_procurement || required) * 100) : 0}%
                                   </span>
                                 </div>
                               </div>
                             </div>

                             {/* Column 4: Outbound */}
                             <div>
                               <div className="flex items-center gap-1 mb-2">
                                 <Truck className="h-4 w-4 text-warning" />
                                 <span className="text-sm font-semibold">出库发货</span>
                               </div>
                               <div className="space-y-1 text-xs">
                                 <div className="flex justify-between">
                                   <span className="text-muted-foreground">已发货</span>
                                   <span className="font-medium text-success">{warehouseData.outbound.shipped_qty}</span>
                                 </div>
                                 <div className="flex justify-between">
                                   <span className="text-muted-foreground">待发货</span>
                                   <span className="text-muted-foreground">{warehouseData.outbound.pending_qty || 0}</span>
                                 </div>
                                  <div className="mt-1">
                                    <Progress
                                      value={required > 0 ? warehouseData.outbound.shipped_qty / required * 100 : 0}
                                      className="h-1"
                                    />
                                    <span className="text-xs text-muted-foreground">
                                      {required > 0 ? Math.round(warehouseData.outbound.shipped_qty / required * 100) : 0}%
                                    </span>
                                  </div>
                                  {inventoryData?.reserved_quantity > 0 && (
                                    <div className="mt-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-center gap-1">
                                      <Snowflake className="h-3 w-3" />
                                      已冻结 {inventoryData.reserved_quantity} 件
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unlink Confirmation Dialog */}
        <Dialog open={unlinkDialog.open} onOpenChange={(open) => !open && setUnlinkDialog({ open: false, po: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>确认操作</DialogTitle>
              <DialogDescription>
                确定要取消关联并删除采购订单「{unlinkDialog.po?.purchase_order_id}」吗？该操作不可撤销。
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUnlinkDialog({ open: false, po: null })}>取消</Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  try {
                    await purchaseService.unlinkPurchaseOrderFromSales(unlinkDialog.po?._id || unlinkDialog.po?.purchase_order_id, true);
                    setUnlinkDialog({ open: false, po: null });
                    await loadExecutionProgress();
                  } catch (err) {
                    alert(err.response?.data?.error || '取消关联失败');
                  }
                }}
              >
                确认删除
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* International Export Status */}
        {order.is_international_trade && (
          <Card className="border-2 border-orange-500 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <h2 className="text-lg font-semibold text-orange-500">🌍 国际出口订单</h2>
                <Badge className="bg-orange-500 text-white">出口管制</Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 border border-orange-500 rounded-lg bg-orange-50">
                  <p className="font-semibold mb-2">🚢 国际运输详情</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span>🇨🇳</span>
                    <span className="text-sm">中国</span>
                    <span className="text-orange-500 font-semibold">→</span>
                    <span className="text-sm font-medium">{order.destination_country || '目的地待确定'}</span>
                  </div>
                  <Badge variant="outline" className={cn(
                    order.export_compliance_status === 'restricted' ? 'border-destructive text-destructive' :
                    order.export_compliance_status === 'license_required' ? 'border-warning text-warning' :
                    order.export_compliance_status === 'approved' ? 'border-success text-success' :
                    'border-info text-info'
                  )}>
                    {order.export_compliance_status === 'restricted' ? '❌ 出口受限' :
                     order.export_compliance_status === 'license_required' ? '📄 需要许可证' :
                     order.export_compliance_status === 'approved' ? '✅ 出口批准' :
                     '⏳ 合规审查中'}
                  </Badge>
                </div>

                <div className="p-4 border border-info rounded-lg bg-info/5">
                  <p className="font-semibold mb-2">📋 出口文件状态</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">✅ 商业发票</div>
                    <div className="flex items-center gap-2">✅ 装箱单</div>
                    <div className="flex items-center gap-2">⏳ 出口许可证</div>
                    <div className="flex items-center gap-2">❌ 原产地证明</div>
                  </div>
                </div>

                {userRole === 'admin' && (
                  <div className="md:col-span-2 p-4 border-2 border-secondary rounded-lg bg-secondary/20 relative">
                    <div className="absolute -top-2 -right-2 bg-secondary text-white px-2 py-1 rounded-full text-xs">🔒</div>
                    <p className="font-semibold text-secondary-foreground mb-4 uppercase tracking-wide">
                      🔧 国际贸易记录（仅管理员）
                    </p>
                    <div className="p-3 bg-warning/10 border border-warning rounded-lg mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span>🔒</span>
                        <span className="font-semibold text-warning">需要管理员权限</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        此订单标记为国际出口。所有贸易记录和敏感数据仅限管理员用户访问。
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold">终端客户：</p>
                        <p className="text-muted-foreground">{order.client_name}</p>
                        {order.destination_country && <p className="text-muted-foreground">{order.destination_country}</p>}
                      </div>
                      <div>
                        <p className="font-semibold">出口分类：</p>
                        <p className="text-muted-foreground">ECCN: 2B001.a (Dual-use)</p>
                        <p className="text-muted-foreground">HS Code: 8501.20.40</p>
                      </div>
                      <div>
                        <p className="font-semibold">出口许可证：</p>
                        <p className="text-muted-foreground">BIS-EXP-2024-{order.sales_order_id?.slice(-3) || '001'}</p>
                        <Badge variant="outline" className="border-warning text-warning mt-1">申请已提交</Badge>
                      </div>
                      <div>
                        <p className="font-semibold">合规状态：</p>
                        <p className="text-muted-foreground">✅ 筛查完成</p>
                        <p className="text-xs text-muted-foreground">最后检查：{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products Table */}
        <Card className="border bg-card mb-6">
          <CardContent className="p-6">
            <ProductTable
              products={order.products || []}
              view="sales"
              readOnly={true}
              title={`产品清单 (${order.products?.length || 0} 项)`}
              canViewPrices={authService.canViewSalesPrices()}
            />
          </CardContent>
        </Card>

        {/* File Attachments */}
        <FileAttachmentManager
          entityType="sales_orders"
          entityId={order?._id}
          title="销售订单附件"
          className="mb-6"
        />

        {/* Action Buttons */}
        <div className="flex justify-between items-center mt-6">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTemplateSelectorOpen(true)}>
              <ExternalLink className="h-4 w-4 mr-2" />
              导出到Excel
            </Button>
            <BulkDownloadProductAttachmentsButton
              entityType="sales_orders"
              entityId={order._id}
              entityName={order.sales_order_id}
            />
          </div>

          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant="outline" onClick={() => navigate('/sales')}>返回订单列表</Button>
            {order.status !== 'invoiced' && (
              <Button variant="outline" onClick={() => handleAction('edit')}>
                <Pencil className="h-4 w-4 mr-2" />
                编辑订单
              </Button>
            )}
            {order.status !== 'invoiced' && (
              <Button variant="outline" onClick={() => handleAction('split')}>
                <Split className="h-4 w-4 mr-2" />
                生成采购订单
              </Button>
            )}
            {order.status !== 'invoiced' && (
              <Button variant="outline" onClick={() => setUnifiedConversionOpen(true)}>
                <Factory className="h-4 w-4 mr-2" />
                生成供应单(采购/生产)
              </Button>
            )}
            {order.status !== 'invoiced' && (
              <Button variant="outline" onClick={() => setLinkPODialog({ open: true })}>
                <Link2 className="h-4 w-4 mr-2" />
                关联现有采购单
              </Button>
            )}
            {order.status === 'confirmed' && (
              <Button variant="outline" className="text-warning border-warning" onClick={() => setOutboundDialogOpen(true)}>
                <Warehouse className="h-4 w-4 mr-2" />
                创建出库单
              </Button>
            )}
            {order.status !== 'confirmed' && order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'invoiced' && (
              <Button className="bg-success hover:bg-success/90 text-white" onClick={() => handleAction('confirm')}>
                <CheckCircle className="h-4 w-4 mr-2" />
                确认订单
              </Button>
            )}
            {order.status === 'shipped' && (
              <Button variant="secondary" onClick={() => handleAction('invoice')}>
                <Receipt className="h-4 w-4 mr-2" />
                已开发票
              </Button>
            )}
          </div>
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: '', order: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmDialog.action === 'ship' ? '确认发货订单' :
                 confirmDialog.action === 'invoice' ? '确认已开发票' : '确认订单'}
              </DialogTitle>
              <DialogDescription>
                {confirmDialog.action === 'ship'
                  ? `您确定要发货订单 ${confirmDialog.order?.sales_order_id} 吗？`
                  : confirmDialog.action === 'invoice'
                    ? `您确定已为订单 ${confirmDialog.order?.sales_order_id} 开具发票吗？这将标记订单为已开票状态。`
                    : `您确定要确认订单 ${confirmDialog.order?.sales_order_id} 吗？这将标记为准备处理。`
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, action: '', order: null })}>取消</Button>
              <Button onClick={handleConfirmAction}>确认</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SO-PO Splitting Dialog */}
        <SoPoSplittingDialog
          open={splittingDialog.open}
          onClose={() => setSplittingDialog({ open: false })}
          onConvert={handleSplittingComplete}
          salesOrderId={order?._id}
          salesOrderData={order}
        />

        <UnifiedConversionDialog
          open={unifiedConversionOpen}
          onClose={() => setUnifiedConversionOpen(false)}
          salesOrderId={order?._id}
          onSuccess={(result) => {
            const messages = [];
            if (result.purchase_orders_created > 0) {
              messages.push(`${result.purchase_orders_created} 个采购订单`);
            }
            if (result.production_orders_created > 0) {
              messages.push(`${result.production_orders_created} 个生产工单`);
            }
            if (result.frozen_stock_count > 0) {
              messages.push(`${result.frozen_stock_count} 个冻结库存`);
            }
            
            if (messages.length === 0) {
              alert('操作成功');
            } else {
              alert(`成功创建: ${messages.join(', ')}`);
            }
            loadExecutionProgress();
          }}
        />

        {/* Link Existing PO Dialog */}
        <LinkPODialog
          open={linkPODialog.open}
          onClose={() => setLinkPODialog({ open: false })}
          onLinked={(result) => {
            setLinkPODialog({ open: false });
            alert(`成功关联 ${result.mappings_created || 0} 条采购订单映射记录`);
            loadExecutionProgress();
          }}
          salesOrderId={order?._id}
          salesOrderData={order}
        />

        {/* Template Selector Dialog */}
        <TemplateSelector
          open={templateSelectorOpen}
          onClose={() => setTemplateSelectorOpen(false)}
          onSelect={handleTemplateSelected}
          entityType="sales"
          language="cn"
          title="选择销售订单导出模板"
        />

        {/* Create Outbound Dialog */}
        <CreateOutboundDialog
          open={outboundDialogOpen}
          onClose={() => setOutboundDialogOpen(false)}
          onSuccess={() => {
            setOutboundDialogOpen(false);
            alert('出库单创建成功！');
            loadExecutionProgress();
          }}
          salesOrderId={order?._id || order?.sales_order_id}
        />

        {/* Product Inventory Drawer */}
        <ProductInventoryDrawer
          open={inventoryDrawer.open}
          onClose={() => setInventoryDrawer({ open: false, productCode: null, productName: null })}
          productCode={inventoryDrawer.productCode}
          productName={inventoryDrawer.productName}
        />
      </div>
    </Layout>
  );
};

export default SalesDetailPage;
